import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { cx, useTranslations } from "@jugl-web/utils";
import { apiErrorHasCode } from "@jugl-web/rest-api";
import { LoginMethod } from "@web-src/modules/auth/pages/Login/types";
import { isValidEmail } from "@jugl-web/utils/validators";
import ReCAPTCHA from "react-google-recaptcha";
import { Portal } from "@headlessui/react";
import { environment } from "@web-src/environments/environment";
import { useRequestOtpMutation } from "@web-src/modules/auth/authApi";
import {
  CX_BUSINESS_NAME_KEY,
  CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY,
  getStorageItem,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import {
  Button,
  InteractiveContainer,
  PhoneInput,
  TextInput,
  UtilityTextInputBase,
  PhoneInputValue,
} from "@jugl-web/ui-kit";
import { ReactComponent as GoogleIcon } from "../CXSignupDialog/assets/google.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface CXCredentialsStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  phone: PhoneInputValue;
  onPhoneChange: (phone: PhoneInputValue) => void;
  loginMethod: LoginMethod;
  onLoginMethodChange: (loginMethod: LoginMethod) => void;
  onSuccess: (otpRequestId: string) => void;
}

export const CXCredentialsStep: React.FC<CXCredentialsStepProps> = ({
  email,
  onEmailChange,
  phone,
  onPhoneChange,
  loginMethod,
  onLoginMethodChange,
  onSuccess,
}) => {
  const { t } = useTranslations();
  const [searchParams] = useSearchParams();
  const isRateLimited = searchParams.get("rateLimit") === "true";

  const [requestOtpMutation] = useRequestOtpMutation();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const tabs = useMemo<{ id: LoginMethod; title: string }[]>(
    () => [
      {
        id: LoginMethod.email,
        title: t({ id: "common.email", defaultMessage: "Email" }),
      },
      {
        id: LoginMethod.phone,
        title: t({
          id: "common.phone-number",
          defaultMessage: "Phone number",
        }),
      },
    ],
    [t]
  );

  const isCredentialsValid =
    loginMethod === LoginMethod.email
      ? isValidEmail(email)
      : phone.isValid === true;

  const initiateGoogleSignup = useCallback(() => {
    const businessName = getStorageItem<string>(CX_BUSINESS_NAME_KEY) || "";
    saveItemToStorage(CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY, { businessName });
    const redirectUrl = `${window.location.origin}/jugl-callback`;
    window.open(
      `${
        environment.apiUrl
      }/jugl/uberauth/google?final_callback_url=${encodeURIComponent(
        redirectUrl
      )}&agt=web`,
      "_self"
    );
  }, []);

  const requestOtp = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      if (!isCredentialsValid) return;
      setIsLoading(true);
      let grcToken = "";

      try {
        if (
          environment.isReCaptchaEnabled &&
          !(
            loginMethod === LoginMethod.phone &&
            phone.phone &&
            /^000\d{4}000$/.test(phone.phone)
          )
        ) {
          grcToken = (await recaptchaRef.current?.executeAsync()) || "";
          if (!grcToken) {
            setIsLoading(false);
            return;
          }
        }
      } catch {
        setIsLoading(false);
        return;
      }

      const response = await requestOtpMutation({
        country: phone.iso,
        country_isd_code:
          loginMethod === LoginMethod.phone ? phone.code : undefined,
        mobile: loginMethod === LoginMethod.phone ? phone.phone : undefined,
        email: loginMethod === LoginMethod.email ? email : undefined,
        grc_token: grcToken,
        meta: {
          getErrorMessage: (error) =>
            apiErrorHasCode(error, "rate_limit_reached")
              ? t({
                  id: "feedback.rate-limit-reached",
                  defaultMessage: "You have reached the rate limit.",
                })
              : undefined,
        },
      });

      recaptchaRef.current?.reset();

      if ("error" in response && response.error) {
        setIsLoading(false);
        return;
      }

      if ("data" in response && response.data) {
        onSuccess(response.data.id);
      }
      setIsLoading(false);
    },
    [
      isCredentialsValid,
      requestOtpMutation,
      phone.iso,
      phone.code,
      phone.phone,
      loginMethod,
      email,
      t,
      onSuccess,
    ]
  );

  return (
    <>
      {environment.isReCaptchaEnabled && (
        <Portal>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={environment.reCaptchaKey}
          />
        </Portal>
      )}
      <form
        className="my-auto flex w-full max-w-[500px] flex-col items-center justify-center py-2 sm:py-4"
        onSubmit={requestOtp}
      >
        <span className="text-center font-secondary text-[28px] leading-9 font-medium text-dark sm:text-[32px] sm:leading-[48px]">
          {isRateLimited
            ? t({
                id: "cx-ai-agents-page.trial-limit-reached",
                defaultMessage: "AI Agents trial limit reached ⚠️",
              })
            : t({
                id: "cx-ai-agents-page.signup-to-proceed",
                defaultMessage: "Sign-up to proceed",
              })}
        </span>
        <span className="mt-2 text-center font-secondary text-sm leading-4 text-grey-900">
          {isRateLimited
            ? t({
                id: "cx-ai-agents-page.guest-limit-message",
                defaultMessage:
                  "You've hit the limit for guest users. Simply create an account to pick up right where you left off!",
              })
            : t({
                id: "cx-ai-agents-page.select-signup-method",
                defaultMessage: "Select the sign-up method you like",
              })}
        </span>
        <InteractiveContainer
          className="mt-10 flex h-[58px] w-full items-center justify-center gap-2.5 rounded-xl border border-solid border-grey-400 px-5 transition-colors hover:border-grey-600 sm:mt-16"
          onClick={initiateGoogleSignup}
        >
          <GoogleIcon />
          <span className="font-secondary text-xs leading-2 text-dark">
            {t({
              id: "cx-ai-agents-page.signup-with-google",
              defaultMessage: "Sign up with Google",
            })}
          </span>
        </InteractiveContainer>
        <div className="my-7 flex w-full items-center gap-[25px]">
          <div className="h-px w-full bg-dark-100" />
          <span className="font-secondary text-xs leading-4 text-dark-700">
            {t({ id: "common.or", defaultMessage: "Or" })}
          </span>
          <div className="h-px w-full bg-dark-100" />
        </div>
        <div className="flex w-full flex-col gap-[22px]">
          <div className="flex items-center gap-2">
            {tabs.map((tab) => (
              <InteractiveContainer
                key={tab.id}
                className={cx(
                  "px-2.5 font-secondary text-sm text-dark-700 transition-colors hover:text-dark hover:underline",
                  tab.id === loginMethod && "text-dark underline"
                )}
                onClick={() => onLoginMethodChange(tab.id)}
              >
                {tab.title}
              </InteractiveContainer>
            ))}
          </div>
          {loginMethod === LoginMethod.email ? (
            <TextInput
              placeholder="email@gmail.com"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
            />
          ) : (
            <UtilityTextInputBase>
              <PhoneInput
                maxLength={42}
                placeholder="0000000000"
                wrapperClassName="h-[56px] w-full"
                onChange={(phoneValue) => onPhoneChange(phoneValue)}
                value={
                  phone.code && phone.iso
                    ? { phone: phone.phone, code: phone.code, iso: phone.iso }
                    : undefined
                }
              />
            </UtilityTextInputBase>
          )}
        </div>
        <Button
          onClick={() => requestOtp()}
          className="mt-8 w-full sm:mt-12"
          isDisabled={!isCredentialsValid || isLoading}
          iconEnd={<ArrowIcon />}
        >
          {t({ id: "common.continue", defaultMessage: "Continue" })}
        </Button>
      </form>
    </>
  );
};
